// @givmo/employer-module — each charity's share of a waiting collection gift's match (EP-WEB40).
//
// D-CAM-111 (Jonathan, 2026-10-07): a charity-collection gift opens its charities, each with its share.
// A waiting request's shares are the server's (Company Profiles' CAM-R136, backend
// `match_collection_label.py` `charity_shares`): the match approving it would book NOW, split over its
// legs as the grouped approval splits it. They are served on `GET /admin/company/matches/pending` (the
// read EP-WEB41's figure now comes from, `current-figures.ts`) and nowhere on the dashboard. Never
// derived here: the split runs over the legs' gifts, which are the employee's and never shown to an
// admin (D-EP-56), and the server places a leftover cent its own way. A request is found by its legs'
// match ids, a share by its leg's. The route is a `-> List[Dict]` one, so its rows are read by hand.
//
// The read is the figure-now read's, bounded the same way, and it decides nothing: a failed, slow or
// unreadable answer shows the charities without their shares. It is never older than the figure it
// is set beside (`useCollectionShares`' `since`).

import { useQuery } from '@tanstack/react-query';
import { extractList, sdk } from '@givmo/api-client';
import { CURRENT_FIGURES_TIMEOUT_MS, matchSetKey } from './current-figures';
import { parseCentsAmount, readMatchFigure, type MatchFigure } from './dashboard-decode';

/** A waiting collection gift as the read lists it now. */
export interface CollectionShares {
  /** What approving the request would match now: the shares add up to it. */
  figure: MatchFigure;
  /** Each leg's share in exact cents, by its match id; null when nothing can be approved. */
  cents: ReadonlyMap<string, number> | null;
}

function readShareCents(raw: unknown): ReadonlyMap<string, number> | null {
  if (!Array.isArray(raw)) return null;
  const cents = new Map<string, number>();
  for (const share of raw) {
    if (share === null || typeof share !== 'object') return null;
    const { match_id: matchId, amount } = share as { match_id?: unknown; amount?: unknown };
    // A Decimal on the wire: a string, or a JSON number from a route that encodes it as one.
    const value = parseCentsAmount(
      typeof amount === 'number'
        ? Number.isFinite(amount)
          ? String(amount)
          : null
        : typeof amount === 'string'
          ? amount
          : null,
    );
    // One share that does not read leaves the request without shares, never a partial split.
    if (typeof matchId !== 'string' || matchId.length === 0 || value === null) return null;
    cents.set(matchId, value);
  }
  return cents;
}

/**
 * Each waiting collection gift's shares from `/pending`'s body, keyed by {@link matchSetKey}. A row
 * with no `collection` block is not a collection gift, and a match set two rows share is left out.
 */
export function readCollectionShares(body: unknown): Map<string, CollectionShares> {
  const requests = new Map<string, CollectionShares>();
  const shared = new Set<string>();
  for (const row of extractList<unknown>(body)) {
    if (row === null || typeof row !== 'object') continue;
    const {
      match_ids: ids,
      collection,
      approvable,
    } = row as {
      match_ids?: unknown;
      collection?: unknown;
      approvable?: unknown;
    };
    if (collection === null || typeof collection !== 'object') continue;
    if (!Array.isArray(ids) || ids.length === 0) continue;
    if (!ids.every((id): id is string => typeof id === 'string' && id.length > 0)) continue;
    const key = matchSetKey(ids);
    if (requests.has(key) || shared.has(key)) {
      requests.delete(key);
      shared.add(key);
      continue;
    }
    requests.set(key, {
      figure: readMatchFigure(approvable),
      cents: readShareCents((collection as { charity_shares?: unknown }).charity_shares),
    });
  }
  return requests;
}

const COLLECTION_SHARES_KEY = 'employer-module-collection-shares';

async function fetchCollectionShares(): Promise<Map<string, CollectionShares>> {
  const read = (async () => {
    const { data } = await sdk.donationMatchesGetPendingMatchesGrouped({ throwOnError: true });
    return readCollectionShares(data);
  })();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const unanswered = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('The shares read did not answer in time.')),
      CURRENT_FIGURES_TIMEOUT_MS,
    );
  });
  try {
    return await Promise.race([read, unanswered]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * A waiting collection gift's shares now, read once its charities are opened (`enabled`):
 * `loading`, `ready` with the request as the read lists it, or `unavailable` — the read failed or was
 * slow, or does not list the request (decided or withdrawn since the list loaded).
 *
 * `since` is when the figure the shares are set beside was read: the list's load, or the dialog's
 * open. The read is keyed on it, so it is never older than that figure. A read kept from before a
 * list refreshed would state the figure the list replaced as the match "now" (cut 31's review, F1).
 */
export function useCollectionShares(
  matchIds: readonly string[],
  enabled: boolean,
  since: number,
):
  | { status: 'loading' }
  | { status: 'ready'; shares: CollectionShares }
  | { status: 'unavailable' } {
  const query = useQuery({
    queryKey: [COLLECTION_SHARES_KEY, since],
    queryFn: fetchCollectionShares,
    enabled,
    retry: false,
    staleTime: 15_000,
  });
  if (query.isPending) return { status: 'loading' };
  const shares = query.data?.get(matchSetKey(matchIds));
  return shares === undefined ? { status: 'unavailable' } : { status: 'ready', shares };
}
